// Client gọi admin-api cho khu Quản trị NPP (docs/KE_HOACH_NPP_SANG_WEB.md §4.1).
// Nghiệp vụ, quyền, phạm vi NPP vẫn ở admin-api — web chỉ là giao diện. Tách hẳn với lib/api.ts:
//   · token quản trị riêng, chỉ giữ trong bộ nhớ;
//   · làm mới bằng cookie httpOnly `sha_rt` qua /admin-api/auth/refresh;
//   · lỗi mang `status` (như trang quản trị cũ) để code chuyển sang dùng y nguyên cách kiểm lỗi.

const ADMIN_API_BASE: string = import.meta.env.VITE_ADMIN_API_BASE ?? '/admin-api'

let accessToken: string | null = null
export function setAdminAccessToken(t: string | null) {
  accessToken = t
}
export function getAdminAccessToken() {
  return accessToken
}

export class AdminApiError extends Error {
  code: string
  status: number
  requestId?: string
  /** Dữ liệu kèm lỗi — ví dụ 409 xoá bảng giá trả danh sách đơn vị đang dùng. */
  details?: unknown
  constructor(status: number, code: string, message: string, requestId?: string, details?: unknown) {
    super(message)
    this.status = status
    this.code = code
    if (requestId !== undefined) this.requestId = requestId
    if (details !== undefined) this.details = details
  }
}

// nppAdmin.ts đăng ký: cập nhật quyền khi token được làm mới ngầm, đọc lại quyền khi gặp 403,
// và khi không làm mới được thì xin lại phiên quản trị từ phiên web. Tách callback để không import vòng.
type RefreshListener = (payload: Record<string, unknown>) => void
let onRefreshed: RefreshListener | null = null
let onForbidden: ((path: string) => void) | null = null
let onExpired: (() => Promise<string | null>) | null = null
export function setAdminRefreshListener(fn: RefreshListener | null) {
  onRefreshed = fn
}
export function setAdminForbiddenListener(fn: ((path: string) => void) | null) {
  onForbidden = fn
}
/** Hết cách làm mới bằng cookie → gọi hàm này (xin lại phiên từ phiên web); trả token mới hoặc null. */
export function setAdminExpiredHandler(fn: (() => Promise<string | null>) | null) {
  onExpired = fn
}

let refreshInFlight: Promise<string | null> | null = null

export async function refreshAdminToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${ADMIN_API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
        credentials: 'include',
      })
      if (res.ok) {
        const json = await res.json()
        accessToken = (json?.accessToken as string) ?? null
        if (accessToken && json && typeof json === 'object') onRefreshed?.(json as Record<string, unknown>)
        if (accessToken) return accessToken
      }
      return onExpired ? await onExpired() : null
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/** Bước xác thực: 401 ở đây là kết quả thật — không làm mới rồi gửi lại. */
function isAuthFlowPath(path: string): boolean {
  return /^\/auth\/(login|refresh|2fa|from-web)(\/|\?|$)/.test(path)
}

async function send(path: string, init: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
    ...(init.headers as Record<string, string> | undefined),
  }
  if (accessToken) headers.authorization = `Bearer ${accessToken}`
  let res = await fetch(`${ADMIN_API_BASE}${path}`, { ...init, headers, credentials: 'include' })
  if (res.status === 401 && !isAuthFlowPath(path)) {
    const refreshed = await refreshAdminToken()
    if (refreshed) {
      headers.authorization = `Bearer ${refreshed}`
      res = await fetch(`${ADMIN_API_BASE}${path}`, { ...init, headers, credentials: 'include' })
    }
  }
  return res
}

async function toError(res: Response, path: string): Promise<AdminApiError> {
  const json = await res.json().catch(() => ({}))
  if (res.status === 403) onForbidden?.(path)
  const err = json?.error ?? { code: 'network_error', message: `HTTP ${res.status}` }
  let message: string = err.message || `HTTP ${res.status}`
  if (Array.isArray(err.details) && err.details[0]?.message) message = err.details[0].message
  return new AdminApiError(res.status, err.code ?? 'unknown', message, err.requestId, err.details)
}

export async function adminApi<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await send(path, init)
  if (res.status === 204) return undefined as T
  if (!res.ok) throw await toError(res, path)
  return (await res.json().catch(() => ({}))) as T
}

/** Phản hồi thô (CSV, file tải về) — cùng token / làm mới phiên; lỗi vẫn ném AdminApiError. */
export async function adminApiRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await send(path, init)
  if (!res.ok) throw await toError(res, path)
  return res
}

const body = (b: unknown) => (b === undefined ? undefined : b instanceof FormData ? b : JSON.stringify(b))

export const adminGet = <T>(p: string) => adminApi<T>(p)
export const adminPost = <T>(p: string, b?: unknown) => adminApi<T>(p, { method: 'POST', body: body(b) })
export const adminPut = <T>(p: string, b?: unknown) => adminApi<T>(p, { method: 'PUT', body: body(b) })
export const adminPatch = <T>(p: string, b?: unknown) => adminApi<T>(p, { method: 'PATCH', body: body(b) })
export const adminDel = <T>(p: string, b?: unknown) => adminApi<T>(p, { method: 'DELETE', body: body(b) })

/** Tải file (CSV, Excel) từ admin-api về máy — kèm token, không mở tab mới. */
export async function adminDownload(path: string, fallbackName: string): Promise<void> {
  const res = await adminApiRaw(path)
  const cd = res.headers.get('content-disposition') ?? ''
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)
  const name = m?.[1] ? decodeURIComponent(m[1]) : fallbackName
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
